/**
 * "Handwritten" — designer Cover 4.
 * Field-notebook feel: handwritten script kicker, italic serif headline,
 * spaced-caps subtitle, italic serif stat numbers with lowercase script
 * labels, and a hand-drawn wavy "смотри дальше" arrow bottom-right.
 * The script family (Caveat) is a fixed part of this design, independent
 * of the user-selected font pairing.
 *
 * Layout reflows: cleared texts are skipped, and the wavy cue yields
 * when long stats need its space.
 */

import { SLIDE_W, SLIDE_H, type CarouselTemplate, type CoverSlideData, type SlideStyle } from "../types";
import {
  drawDesignerBackdrop, drawMockScrim, drawWatermark,
  drawTrackedText, drawStatRow, drawSerifHeadline,
} from "../shared";
import { SCRIPT_FONT, NUMBER_FONT } from "../fonts";
import { fieldDiary } from "./field-diary";

function drawWavyArrow(ctx: CanvasRenderingContext2D, x0: number, x1: number, y: number): void {
  ctx.save();
  ctx.lineWidth = 5;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(x0, y + 8);
  ctx.bezierCurveTo(x0 + 70, y + 24, x0 + 140, y - 16, x1 - 8, y);
  ctx.stroke();
  // hand-drawn arrowhead, slightly asymmetric
  ctx.beginPath();
  ctx.moveTo(x1 - 30, y - 22);
  ctx.lineTo(x1, y - 1);
  ctx.lineTo(x1 - 26, y + 14);
  ctx.stroke();
  ctx.restore();
}

function drawCover(ctx: CanvasRenderingContext2D, data: CoverSlideData, style: SlideStyle): void {
  const W = SLIDE_W, H = SLIDE_H, PAD = 64;
  const DISPLAY = style.fonts.display;
  const BODY = style.fonts.body;

  ctx.clearRect(0, 0, W, H);
  const textOnLight = drawDesignerBackdrop(ctx, data);
  drawMockScrim(ctx, 650, true, !textOnLight);
  drawMockScrim(ctx, 300, false, !textOnLight);

  const rgb = textOnLight ? "28,36,32" : "255,255,255";
  const solid = textOnLight ? "#1c2420" : "#ffffff";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";

  // ── Script kicker (keeps the user's own casing; optional) ──
  const kicker = data.kickerText.trim();
  const kickerSize = 46 * (data.kickerScale ?? 1);
  if (kicker) {
    ctx.font = `400 ${kickerSize}px ${SCRIPT_FONT}`;
    ctx.fillStyle = `rgba(${rgb},0.56)`;
    ctx.fillText(kicker, PAD, 99 + kickerSize);
  }

  // ── Italic serif headline (design size 340 at the default 140 slider) ──
  const size = 340 * (data.headlineFontSize / 140);
  const capTop = kicker ? 99 + kickerSize + 25 : 96;
  ctx.fillStyle = solid;
  const lastBaseline = drawSerifHeadline(ctx, data.headlineTitle, PAD, 0, W - PAD * 2, size, DISPLAY, {
    italic: true,
    capTop,
  });

  // ── Subtitle (optional) ──
  const subtitle = data.subtitle.trim();
  if (subtitle) {
    const s = data.subtitleScale ?? 1;
    ctx.font = `300 ${46 * s}px ${BODY}`;
    ctx.fillStyle = `rgba(${rgb},0.76)`;
    drawTrackedText(ctx, subtitle.toUpperCase(), PAD, lastBaseline + 74 * Math.max(s, 0.8), 12 * s);
  }

  // ── Stats: italic serif numbers, lowercase script labels ──
  const st = data.statsScale ?? 1;
  const statsEnd = drawStatRow(ctx, data.stats, {
    x0: PAD,
    numBase: 1229,
    labelBase: 1229 + 71 * st,
    numFont: `italic 400 ${84 * st}px ${NUMBER_FONT}`,
    textFont: `italic 400 ${48 * st}px ${DISPLAY}`,
    labelFont: `400 ${34 * st}px ${SCRIPT_FONT}`,
    labelTracking: 0,
    lowercaseLabels: true,
    labelAlpha: 0.52,
    solid,
    rgb,
    divider: { top: 1173, height: 68, alpha: 0.2 },
    segGap: 52,
  });

  // ── Swipe cue + wavy arrow, bottom-right — yields to long stats ──
  const cue = data.swipeText?.trim() || "смотри дальше";
  ctx.font = `400 42px ${SCRIPT_FONT}`;
  const cueW = ctx.measureText(cue).width;
  const cueStart = W - PAD - cueW;
  if (statsEnd < Math.min(cueStart, 768) - 40) {
    ctx.fillStyle = `rgba(${rgb},0.62)`;
    ctx.fillText(cue, cueStart, 1198);
    ctx.strokeStyle = solid;
    drawWavyArrow(ctx, 768, 1012, 1252);
  }

  drawWatermark(ctx, style, textOnLight);
}

export const handwritten: CarouselTemplate = {
  id: "handwritten",
  name: "Handwritten",
  premium: false,
  drawCover,
  drawSpecies: fieldDiary.drawSpecies,
  drawClosing: fieldDiary.drawClosing,
};
